/* eslint-env node,browser */
import _ from 'lodash';
import Combinatorics from 'js-combinatorics';
import { nanoid } from 'nanoid';

import SharedConfig from 'shared/config';
import { DEVMODE } from 'shared/util';

import {
  ALGARI_COLOR_COMBO_MAX,
  SOCKET_TYPES,
} from 'shared/constants';
import SimcInput, { COMMANDS_GEAR_ALIASES } from 'shared/simcinput';
import {
  itemSocketInfo,
  isValidEnhancementForItem,
  getWeaponHands,
  canAddSocket,
  addPrismaticSocket,
} from 'shared/items';
import { tagByAddonCategory } from 'shared/itemTags';
import { getSpecInfo } from 'shared/character';
import getTalentCombinations from 'shared/talentCombinations';
import { getEnchant } from 'shared/enchants';
import {
  getGemDetails,
  getGemDetailsByEnchantId,
} from 'shared/gems';

function getNow() {
  if (typeof window !== 'undefined' && window.performance) {
    return window.performance.now();
  }
  return Date.now();
}

// eslint-disable-next-line no-unused-vars
function devLog(...args) {
  if (DEVMODE) {
    // eslint-disable-next-line no-console
    console.log(...args);
  }
}
// eslint-disable-next-line no-unused-vars
function devTable(data) {
  if (DEVMODE) {
    // eslint-disable-next-line no-console
    console.table(data);
  }
}

export function numLegendaries(items) {
  return _.reduce(items, (res, it) => {
    if (_.isArray(it)) {
      return res + numLegendaries(it);
    }
    return res;
  }, 0);
}

export function numItemSet(items, itemSetIds) {
  return _.reduce(items, (res, it) => {
    if (_.isArray(it)) {
      // skip rings/trinkets for now
      // TODO: if this needs to be done at some point, need to merge the results from a recursive call
      return res;
    }
    const { itemSetId } = it;
    if (itemSetId && _.includes(itemSetIds, itemSetId)) {
      if (!res[itemSetId]) {
        res[itemSetId] = 0;
      }
      res[itemSetId] += 1;
    }
    return res;
  }, {});
}

export function inputSource(input) {
  if (input) {
    if (input.match(/\[Profile\]/)) {
      return 'simpermut';
    }
    return 'simc';
  }
  return null;
}

export function transformInputToData(input) {
  if (!input) {
    return null;
  }
  let data = SimcInput.parse(input);
  if (inputSource(input) === 'simpermut') {
    const name = SimcInput.find(data, 'profilename');
    const charClass = SimcInput.find(data, 'class');

    data = SimcInput.reject(data, '[Profile]');
    data = SimcInput.reject(data, 'profilename');
    data = SimcInput.reject(data, 'profileid');
    data = SimcInput.reject(data, 'class');
    data = SimcInput.reject(data, 'position');
    data = SimcInput.reject(data, 'other');
    data = SimcInput.reject(data, '[Gear]');
    data = SimcInput.prepend(data, charClass, name);
    data = SimcInput.prependComment(data, 'Input from SimPermut');

    // handle inconsistent 2 ring/trinket autosimc output
    // merge the two items into finger1/trinket1 like /simc gear from bags
    const finger1 = SimcInput.find(data, 'finger1');
    if (finger1.split('|').length === 1) {
      const finger2 = SimcInput.find(data, 'finger2');
      data = SimcInput.set(data, 'finger1', [finger1, finger2].join('|'));
    }
    const trinket1 = SimcInput.find(data, 'trinket1');
    if (trinket1.split('|').length === 1) {
      const trinket2 = SimcInput.find(data, 'trinket2');
      data = SimcInput.set(data, 'trinket1', [trinket1, trinket2].join('|'));
    }
    data = SimcInput.reject(data, 'finger2');
    data = SimcInput.reject(data, 'trinket2');
  }
  return data;
}

function parseItemAttributes(item) {
  const rawAttrs = item.split(',');
  const attributes = _.fromPairs(_.tail(rawAttrs).map(attr => attr.split('=')));
  return attributes;
}

export function makeGearOptions(data) {
  if (!data || data.length === 0) {
    return {};
  }
  const options = {};
  // simpermut has the same data in finger1 and finger2. same for trinkets
  const slots = [
    'head', 'neck', 'shoulder', 'back', 'chest', 'wrist', 'hands', 'waist', 'legs', 'feet',
    'finger1', 'finger2', 'trinket1', 'trinket2', 'main_hand', 'off_hand',
    'unknown',
  ];

  _.each(slots, slot => {
    options[slot] = [];
    const line = SimcInput.find(data, slot);
    if (line) {
      // handles both normal simc equipped gear input and all simpermut gear input
      const items = line.split('|');
      _.each(items, item => {
        const attributes = parseItemAttributes(item);
        // for simpermut, treat everything as equipped to ensure all items are checked on init
        attributes.equipped = true;
        attributes.equippedSlot = slot;
        options[slot].push(attributes);
      });
    }
  });

  // find items in comments
  let source;
  _.each(
    _.filter(data, line => line.comment),
    line => {
      const trimmed = _.trim(line.comment);
      // try to parse
      const [command, ...rest] = trimmed.split('=');
      const slot = COMMANDS_GEAR_ALIASES[command] || command;
      if (_.includes(slots, slot)) {
        const rawAttrs = rest.join('=');
        const attrs = parseItemAttributes(rawAttrs);
        if (source) {
          if (!attrs.tags) {
            attrs.tags = [];
          }
          attrs.tags.push(source);
          // backwards compat
          attrs.source = source;
        }
        options[slot].push(attrs);
      } else if (_.startsWith(trimmed, '##')) {
        // section delimeter
        const category = _.trim(trimmed, '# ');
        const tag = tagByAddonCategory[category];
        if (tag) {
          source = tag.key;
        }
      }
    },
  );

  // merge rings and trinkets

  options.rings = _.union(options.finger1, options.finger2);
  delete options.finger1;
  delete options.finger2;

  options.trinkets = _.union(options.trinket1, options.trinket2);
  delete options.trinket1;
  delete options.trinket2;

  return options;
}

export function isGearValidForOptimize(gear) {
  const inclusiveMinimums = {
    head: 1,
    neck: 1,
    shoulder: 1,
    back: 1,
    chest: 1,
    wrist: 1,
    hands: 1,
    waist: 1,
    legs: 1,
    feet: 1,
    main_hand: 1,
    rings: 2,
    trinkets: 2,
  };

  const valid = _.reduce(
    _.mapValues(inclusiveMinimums, (value, slot) => {
      const result = gear[slot] && (gear[slot].length >= value);
      return result;
    }),
    (result, aboveMin) => result && aboveMin,
    true,
  );

  return valid;
}

export function getSelectedGear(
  gear,
  selected,
) {
  // save some indices
  const result = _.mapValues(gear, (items, slot) => {
    return _.filter(
      // save original index
      _.map(items, (item, index) => {
        return {
          ...item,
          origIdx: index,
          selectedIdx: index,
        };
      }),
      // filter selected items
      (item, index) => _.includes(selected[slot], index),
    );
  });

  return result;
}

function getTalentTreeCombinations(talentLoadouts, level) {
  const selectedLoadouts = _.filter(talentLoadouts, loadout => {
    return loadout.selected || loadout.active;
  });

  const loadoutStrings = [];
  _.each(selectedLoadouts, loadout => {
    if (loadout.source === 'input') {
      loadoutStrings.push(loadout.string);
    } else {
      try {
        if (loadout.generateCombos) {
          const loadoutCombos = getTalentCombinations(
            loadout.talents,
            level,
            {
              exclude: loadout.exclude,
            },
          );

          _.each(loadoutCombos, combo => loadoutStrings.push(combo));
        } else {
          loadoutStrings.push(loadout.string);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        throw error;
      }
    }
  });

  return loadoutStrings;
}

function getGemCombinationsDirect(gems, forceMainStat, socketLayout) {
  if (socketLayout.length === 0) {
    return [];
  }

  const socketType = socketLayout[0];
  let validGems = gems[socketType];

  if (_.size(validGems) === 0) {
    validGems = [{
      enhancementId: null,
      type: socketType,
      gem: { name: 'EMPTY' },
    }];
  }

  if (socketLayout.length === 1) {
    return _.map(
      // make sure we only send back 1 unique gem if forceMainStat is on
      _.filter(validGems, g => !forceMainStat || g.gem.type !== 'PRISMATIC' || g.unique),
      g => [{ gem: g.enhancementId, type: socketType, gemDetail: g }],
    );
  }

  const results = [];

  for (let i = 0; i < validGems.length; i += 1) {
    const fixedGem = validGems[i];

    let nextGems = _.mapValues(gems, (typeGems, type) => {
      if (type === socketType) {
        return _.drop(typeGems, i);
      }

      return typeGems;
    });

    if (forceMainStat && fixedGem.gem.type === 'PRISMATIC' && !fixedGem.unique) {
      // eslint-disable-next-line no-continue
      continue;
    }

    // drop from next slot recusive call
    if (fixedGem.unique) {
      nextGems = _.mapValues(nextGems, (typeGems, type) => {
        if (type === socketType) {
          return _.reject(typeGems, { unique: fixedGem.unique });
        }
        return typeGems;
      });
    }

    // keep this true if we process a non-prismatic first
    const nextForceMainStat = fixedGem.gem.type === 'PRISMATIC' ? false : forceMainStat;

    const subCombos = getGemCombinationsDirect(nextGems, nextForceMainStat, socketLayout.slice(1));
    _.each(subCombos, combo => {
      results.push([
        {
          gem: fixedGem.enhancementId,
          type: socketType,
          gemDetail: fixedGem,
        },
        ...combo,
      ]);
    });
  }

  return results;
}

// good god, so many dragons
export function getGemCombinations(
  selectedGems,
  forceMainStat,
  socketLayout,
  simpleMode,
) {
  const gems = _.reverse(
    _.sortBy(
      _.map(
        selectedGems,
        gem => {
          return gem;
        },
      ),
      'unique',
    ),
  );
  if (_.size(gems) === 0) {
    return [];
  }

  const gemsByType = _.groupBy(gems, 'gem.type');

  // add a check to make sure that a main stat gem is actually selected before applying forceMainStat
  const forceMain = forceMainStat && Boolean(_.find(gems, g => g.gem.type === 'PRISMATIC' && g.unique));

  let validGemSets = [];
  if (simpleMode) {
    // ironic how much more complicated this mode is

    // we want one of each 'unique' type. group them and cartesian product
    const gemsByUniqueAndType = _.groupBy(gems, g => `${g.gem.type}-${g.unique}`);
    const simpleGemLayouts = Combinatorics.cartesianProduct(..._.values(gemsByUniqueAndType)).toArray();
    for (let i = 0; i < simpleGemLayouts.length; i += 1) {
      // need to regroup by gem type for getGemCombinationsDirect
      const regroupByType = _.groupBy(simpleGemLayouts[i], 'gem.type');

      // concat these results together
      validGemSets = _.concat(
        validGemSets,
        getGemCombinationsDirect(
          regroupByType,
          forceMain,
          socketLayout,
        ),
      );
    }
  } else {
    validGemSets = getGemCombinationsDirect(gemsByType, forceMain, socketLayout);
  }

  // find minimum number of empty sockets
  const gemSetsByEmptySockets = _.groupBy(
    validGemSets,
    vgs => _.reduce(vgs, (empties, gem) => (gem.gem === null ? empties + 1 : empties), 0),
  );

  // only keep gem combos with min number of empty sockets
  const leastEmpties = gemSetsByEmptySockets[_.min(_.keys(gemSetsByEmptySockets))];

  const result = _.sortBy(leastEmpties, 'type');

  return result;
}

const getParsed = item => {
  if (item) {
    const gems = _.filter(
      _.map(
        _.split(item.gem_id, '/'),
        gid => {
          if (gid) {
            const gem = getGemDetails(parseInt(gid));
            return gem;
          }
          return null;
        },
      ),
    );

    let enchant;
    let enchantId;
    if (item.enchant_id) {
      enchantId = parseInt(item.enchant_id, 10);
      enchant = getEnchant(enchantId);
    }

    return {
      enchant_id: enchantId,
      enchant,
      gems,
      uniqueGems: _.filter(gems, 'unique'), // denormalized for lookup speed later
    };
  }
  return undefined;
};
const makeNewCacheItem = item => {
  return {
    ...item,
    cacheGuid: nanoid(),
  };
};
const decorateItem = item => {
  if (item) {
    return makeNewCacheItem({
      ...item,
      parsed: getParsed(item),
      socketInfo: itemSocketInfo(item),
    });
  }
  return null;
};

const getGemItem = _.memoize((item, gemStr) => {
  // do some careful copying here for perf
  const override = {
    ...item.override,
    gem_id: gemStr,
  };
  const newItem = decorateItem({
    ...makeNewCacheItem(item),
    gem_id: gemStr,
    override,
  });
  return newItem;
}, (item, gemStr) => `${item.cacheGuid}-${gemStr}`);

export function getRawCombinations(
  gear,
  gem,
  cloakEnchant,
  ringEnchant,
  neckEnchant,
  weaponEnchant,
  weaponOffhandEnchant,
  replaceExisting,
  classId,
  specName,
  selectedEnhancements,
  enhancementReplace,
  addSocketNum,
  addSocketGem,
  upgradeCurrencyConstraints,
  talentLoadouts,
  level,
  catalystCharges,
) {
  const slots = [
    'head', 'neck', 'shoulder', 'back', 'chest', 'wrist', 'hands', 'waist',
    'legs', 'feet', 'rings', 'trinkets', 'main_hand', 'off_hand',
  ];
  if (!gear) {
    return {
      combinations: [],
    };
  }
  const requiredSlots = _.pull(slots, 'off_hand');
  const gearPopulated = _.reduce(requiredSlots, (res, slot) => res && gear[slot] && gear[slot].length > 0, true);
  if (!gearPopulated) {
    return {
      combinations: [],
    };
  }

  const specDetails = getSpecInfo(classId, specName) || {};

  const enhancementsBySlot = _.groupBy(_.filter(selectedEnhancements, { specId: specDetails.id }), 'slot');

  // update socket info for later
  let trimmedGear = _.mapValues(gear, items => _.map(items, item => {
    const customItemLimits = [];
    // only allow a single weekly reward item in a combination
    // TODO: Remove item.source
    if (_.includes(item.tags, 'weekly_reward') || item.source === 'weekly_reward') {
      const customItemLimit = {
        category: 'weekly_reward',
        quantity: 1,
        value: 1,
      };
      // In Dragonflight Season 4, you can buy two sockets from one Great Vault
      if (_.includes(item.tags, 'add-gv-socket')) {
        customItemLimit.value = 1;
      }
      customItemLimits.push(customItemLimit);
    }
    // Configurable limit on how many catalyst charges to use
    if (_.includes(item.tags, 'catalyst')) {
      customItemLimits.push({
        category: 'catalyst',
        quantity: catalystCharges || 0,
      });
    }

    return {
      ...decorateItem(_.omit(item, [
        'chanceBonusList',
        'names',
        'bonusLists',
      ])),
      // assign a unique guid if item doesn't already have one (weapons will have guids)
      // This will be checked later for ring/trinket combos to make sure we don't re-use the same item twice with
      // different gem/enchants
      guid: item.guid || nanoid(),
      cacheGuid: nanoid(),
      socketInfo: itemSocketInfo(item),
      canAddSocket: canAddSocket(item, SOCKET_TYPES.PRISMATIC),
      customItemLimits,
    };
  }));

  const enchantSlotMap = {
    rings: 'finger',
  };

  // setup enchant overrides
  trimmedGear = _.mapValues(trimmedGear, (items, slot) => {
    const enchantSlot = enchantSlotMap[slot] || slot;
    if (enhancementsBySlot[enchantSlot]) {
      const slotItems = [];
      _.each(items, item => {
        if (enhancementReplace || !item.enchant_id) {
          _.each(enhancementsBySlot[enchantSlot], enchant => {
            if (isValidEnhancementForItem(enchant, item)) {
              // don't use makeNewCacheItem here, this is a modification of an existing item
              const newItem = decorateItem({
                ...item,
                enchant_id: enchant.enhancementId,
                override: {
                  enchant_id: enchant.enhancementId,
                },
              });
              slotItems.push(newItem);
            }
          });
        } else {
          slotItems.push(item);
        }
      });
      return slotItems;
    }
    return items;
  });

  // add prismatic sockets
  const addSocketGemId = _.get(_.find(addSocketGem, { specId: specDetails.id }), 'gemId');
  if (addSocketNum && addSocketGemId) {
    trimmedGear = _.mapValues(trimmedGear, (items) => {
      const slotItems = [];
      _.each(items, item => {
        slotItems.push(item);
        if (canAddSocket(item, SOCKET_TYPES.PRISMATIC)) {
          let newItem = makeNewCacheItem(item);
          newItem = addPrismaticSocket(newItem, SOCKET_TYPES.PRISMATIC);

          const gemId = addSocketGemId;
          newItem.equipped = false;
          newItem.hasSockets = true;
          newItem.gem_id = gemId;
          newItem.override = {
            ...newItem.override,
            gem_id: gemId,
            addSocket: true,
          };
          // fake an item limit category
          newItem.customItemLimits = [
            ...newItem.customItemLimits,
            {
              category: 'addSocket',
              quantity: addSocketNum,
            },
          ];
          slotItems.push(decorateItem(newItem));
        }
      });
      return slotItems;
    });
  }

  if (!trimmedGear.rings || trimmedGear.rings.length < 2) {
    return {
      combinations: [],
    };
  }
  if (!trimmedGear.trinkets || trimmedGear.trinkets.length < 2) {
    return {
      combinations: [],
    };
  }

  // figure out valid weapon combos

  if (!trimmedGear.main_hand) {
    return {
      combinations: [],
    };
  }


  //-----------------------------------
  // weapon permutations

  const specCanDualWield = specDetails.dualWield;
  const specTwoHandersInOneHand = specDetails.dualWield2h;

  const rawWeaponProduct = Combinatorics.cartesianProduct(trimmedGear.main_hand, [...trimmedGear.off_hand, null]);
  // filter raw weapon product to usable combos

  const weaponPermutations = _.filter(rawWeaponProduct.toArray(), weaponSet => {
    const [mainHand, offHand] = weaponSet;
    const mainHandInfo = getWeaponHands(mainHand);

    // don't re-use the same weapon
    if (offHand && mainHand.guid && offHand.guid && mainHand.guid === offHand.guid) {
      return false;
    }

    // dual wielders should use two weapons
    if (mainHandInfo !== '2h' && specCanDualWield && offHand === null) {
      return false;
    }

    // remove 2h + 1h combos for classes that can't use
    if (mainHandInfo === '2h' && offHand !== null && !specTwoHandersInOneHand) {
      return false;
    }

    // remove empty off hand for fury warriors
    if (specTwoHandersInOneHand && offHand === null) {
      return false;
    }

    // fill both slots
    if (_.includes(['mh', '1h'], mainHandInfo) && offHand === null) {
      return false;
    }

    // reject artifact/non-artifact in same combo
    if (mainHand && offHand) {
      if (mainHand.quality === 6 || offHand.quality === 6) {
        if (mainHand.quality !== offHand.quality) {
          return false;
        }
      }
    }

    return true;
  });

  if (weaponPermutations.length === 0) {
    return {
      combinations: [],
    };
  }

  //-----------------------------------
  // generate talent combinations

  // TODO: shift talent combos to getCombinations to avoid reprocessing a bunch of gem filling for better perf

  let talentCombos = null;
  if (talentLoadouts) {
    talentCombos = getTalentTreeCombinations(talentLoadouts, level);
  }

  //-----------------------------------
  // create the raw cartesian product

  try {
    // get ring and trinket combos, filter out items that got duped because of gem/enchant shenanigans
    const ringCombos = _.reject(
      Combinatorics.bigCombination(trimmedGear.rings, 2).toArray(),
      ([ring1, ring2]) => ring1.guid === ring2.guid,
    );
    if (ringCombos.length === 0) {
      return {
        combinations: [],
      };
    }
    const allTrinketsEquipped = _.every(trimmedGear.trinkets, { equipped: true });
    const trinketCombos = _.reduce(
      _.reject(
        Combinatorics.bigCombination(trimmedGear.trinkets, 2).toArray(),
        ([trinket1, trinket2]) => trinket1.guid === trinket2.guid,
      ),
      (result, [trinket1, trinket2]) => {
        result.push([trinket1, trinket2]);
        // make sure double 'on use' trinkets are tested in both slots. First trinket will get more bloodlust time,
        // variations might correspond better with spec cooldowns, etc
        if (!allTrinketsEquipped && trinket1.onUseTrinket && trinket2.onUseTrinket) {
          result.push([{
            ...trinket2,
            equipped: false,
          }, {
            ...trinket1,
            equipped: false,
          }]);
        }
        return result;
      },
      [],
    );

    if (trinketCombos.length === 0) {
      return {
        combinations: [],
      };
    }

    const cpArgs = [
      trimmedGear.head,
      trimmedGear.neck,
      trimmedGear.shoulder,
      trimmedGear.back,
      trimmedGear.chest,
      trimmedGear.wrist,
      trimmedGear.hands,
      trimmedGear.waist,
      trimmedGear.legs,
      trimmedGear.feet,
      ringCombos,
      trinketCombos,
      weaponPermutations,
    ];

    if (talentCombos) {
      cpArgs.push(talentCombos);
    }

    /*
    if (cp.length > SharedConfig.optimize.maxRawCombinations) {
      console.log('too many raw combos', cp.length, SharedConfig.optimize.maxRawCombinations);
      return null;
    }
    */

    /*
     * Depth first search of possible gearsets
     * Try to prune branches as quickly as possible rather than doing a full cartesian product and then iterating
     * over everything and filtering things out after-the-fact
     *
     * Takes in a strategy that sorts the slots to experiment to find the best slot order to search.
     * Pruning checks performned:
     *  Reject sets with two unique items
     *  Reject sets that violate category item limits
     *  Reject sets with two unique gems
    */
    let walkCalls = 0;
    let partialsExplored = 0;
    let leavesExplored = 0;
    let tooLarge = false;
    // eslint-disable-next-line no-unused-vars
    let rejections;
    // eslint-disable-next-line no-unused-vars
    let rejectedAt;
    const getCombos = (rawData, {
      sortStrategy = null,
    }) => {
      // TODO: Figure out if there's some smart way to estimate the size here
      const combinations = new Array(10000);
      let nextCombo = 0;
      let calls = 0;
      let partials = 0;
      let leaves = 0;
      const internalRejections = {
        customItemLimits: 0,
        uniqueEquipped: 0,
        itemLimits: 0,
        uniqueGems: 0,
        overUpgradeBudget: 0,
        overBudgetCurrencies: new Set(),
        notEnoughAlgariColors: 0,
      };
      const internalRejectedAt = _.map(_.range(0, 15), () => 0);

      const maxLen = rawData.length;

      let data;
      if (sortStrategy) {
        const withOrigIdx = _.map(rawData, (el, idx) => ({ data: el, origIdx: idx }));
        data = sortStrategy(withOrigIdx);
      } else {
        data = rawData;
      }

      /*
       * WARNING!
       *
       * This is a heavily optimized piece of code that is easy to screw up.
       * Don't be an idiot, be careful with it.
       */

      const walk = (
        left,
        candidate = [],
        candidateLen = 0,
        uniqueIds = new Set(),
        itemLimitCounts = new Map(),
        uniqueGems = new Set(),
        upgradeCosts = new Map(),
      ) => {
        calls += 1;
        if (
          tooLarge
          || nextCombo > SharedConfig.optimize.maxRawCombinations
          || partials > SharedConfig.optimize.maxWalkPartials
        ) {
          tooLarge = true;
        } else if (candidateLen === maxLen) {
          leaves += 1;
          // Only add sets that are fully socketed or have added a socket
          if (addSocketNum && addSocketGemId) {
            let hasAdded = false;
            let canSocket = 0;
            let socketed = 0;
            for (let i = 0; i < candidateLen; i += 1) {
              const it = candidate[i];
              if (it.override && it.override.addSocket) {
                hasAdded = true;
              }
              if (it.canAddSocket) {
                canSocket += 1;
              }
              if (it.socketInfo?.PRISMATIC?.total > 0) {
                socketed += 1;
              }
            }
            if (socketed < canSocket && !hasAdded) {
              return;
            }
          }
          combinations[nextCombo] = candidate;
          nextCombo += 1;
        } else {
          const newLeft = left.slice(1);

          for (let i = 0; i < left[0].data.length; i += 1) {
            partials += 1;
            const newItem = left[0].data[i];

            // Warning, heavy optimization ahead!
            // Most of these are empty placeholder, we only copy if we need to make a change
            // This should reduce a huge amount of object copy churn for no real need
            let nextItemLimitCounts;
            let nextUniqueIds;
            let nextUniqueGems;
            let nextUpgradeCosts;

            if (newItem) {
              // convert to array
              let items;
              if (newItem.length) {
                items = newItem;
              } else {
                items = [newItem];
              }

              let reject = false;

              // rings and trinkets will be two-fer
              for (let j = 0; j < items.length; j += 1) {
                const item = items[j];

                if (!item) {
                  // eslint-disable-next-line no-continue
                  continue;
                }

                /*
                 * Check on various validity constraints. Order here could be very important
                 * as this is a very tight loop that might be executed millions of times so
                 * the first checks should do as much as possible.
                 *
                 * TODO: dynamic ordering of checks based on previous rejections?
                 */

                //////////////////////////////////////////////
                // custom item limit checks
                //////////////////////////////////////////////

                // item limits for things like add socket and preserved contaminant
                if (item.customItemLimits) {
                  // make sure we only create once in case of ring/trinket loop
                  if (!nextItemLimitCounts) {
                    nextItemLimitCounts = new Map(itemLimitCounts);
                  }
                  // It's ok to ignore this, reject is checked outside the loop
                  // eslint-disable-next-line no-loop-func
                  for (let limitIdx = 0; limitIdx < item.customItemLimits.length; limitIdx += 1) {
                    const { category, quantity, value } = item.customItemLimits[limitIdx];
                    const itemValue = value ?? 1;
                    const newValue = itemValue + (nextItemLimitCounts.get(category) || 0);
                    if (newValue > quantity) {
                      reject = true;
                      internalRejections.customItemLimits += 1;
                      break;
                    }
                    nextItemLimitCounts.set(category, newValue);
                  }
                  if (reject) {
                    break;
                  }
                }


                //////////////////////////////////////////////
                // unique equipped check
                //////////////////////////////////////////////

                // unique equipped
                if (item.uniqueEquipped) {
                  // make sure we only create once in case of ring/trinket loop
                  if (!nextUniqueIds) {
                    nextUniqueIds = new Set(uniqueIds);
                  }
                  if (nextUniqueIds.has(item.id)) {
                    reject = true;
                    internalRejections.uniqueEquipped += 1;
                    break;
                  } else {
                    nextUniqueIds.add(item.id);
                  }
                }


                //////////////////////////////////////////////
                // item limit check
                //////////////////////////////////////////////

                // item limits
                if (item.itemLimit) {
                  // make sure we only create once in case of ring/trinket loop
                  if (!nextItemLimitCounts) {
                    nextItemLimitCounts = new Map(itemLimitCounts);
                  }

                  const category = item.itemLimit.category;
                  const newValue = 1 + (nextItemLimitCounts.get(category) || 0);

                  if (newValue > item.itemLimit.quantity) {
                    reject = true;
                    internalRejections.itemLimits += 1;
                    break;
                  }
                  nextItemLimitCounts.set(category, newValue);
                }


                //////////////////////////////////////////////
                // unique gem check
                //////////////////////////////////////////////

                // unique gems
                // Category uniqueness is manually entered in items.js with either an itemLimitCategory
                // or a fake string name for the gem uniqueness group (Domination gems)

                // only run this if we're not replacing gems.
                // Otherwise, can have the situation where you have a unique gem equipped but want to compare against
                // other items that have another unique. This would block those from getting to the gem stuff in the
                // second phase of combinations
                if (!enhancementReplace) {
                  // eslint-disable-next-line no-loop-func
                  _.each(item.socketInfo, (socketInfo, socketType) => {
                    if (socketInfo.total > 0) {
                      const socketTypeUniqueGems = _.filter(item.parsed.uniqueGems, { type: socketType });
                      if (socketTypeUniqueGems.length > 0) {
                        if (!nextUniqueGems) {
                          nextUniqueGems = new Set(uniqueGems);
                        }
                        for (let g = 0; g < socketTypeUniqueGems.length; g += 1) {
                          const uniqueGem = socketTypeUniqueGems[g];
                          if (uniqueGem.type === socketType) {
                            if (nextUniqueGems.has(uniqueGem.unique)) {
                              reject = true;
                              internalRejections.uniqueGems += 1;
                              return false;
                            }
                            nextUniqueGems.add(uniqueGem.unique);
                          }
                        }
                      }
                    }
                    return true;
                  });
                }
                if (reject) {
                  break;
                }

                ///////////////////////////////////////////////////
                // Item Upgrade currency constraint check
                ///////////////////////////////////////////////////

                if (item.upgradeCost) {
                  for (let k = 0; k < item.upgradeCost.length; k += 1) {
                    const upgradeCost = item.upgradeCost[k];
                    const currencyId = upgradeCost.currencyId || upgradeCost.itemId;
                    const alternateCurrencyId = upgradeCost.alternateItemId;

                    const budget = upgradeCurrencyConstraints[currencyId];

                    if (!nextUpgradeCosts) {
                      nextUpgradeCosts = new Map(upgradeCosts);
                    }

                    const curCost = nextUpgradeCosts.get(currencyId) || 0;
                    const newCost = curCost + upgradeCost.amount;

                    let newAlternateCost;
                    let canAfford = false;
                    let usedAlternate = false;

                    if (newCost <= budget) {
                      canAfford = true;
                    } else if (alternateCurrencyId) {
                      usedAlternate = true;

                      const alternateBudget = upgradeCurrencyConstraints[alternateCurrencyId];

                      const curAlternateCost = nextUpgradeCosts.get(alternateCurrencyId) || 0;
                      newAlternateCost = curAlternateCost + upgradeCost.alternateItemAmount;

                      canAfford = newAlternateCost <= alternateBudget;
                    }

                    if (canAfford) {
                      nextUpgradeCosts.set(currencyId, newCost);
                      if (usedAlternate) {
                        nextUpgradeCosts.set(alternateCurrencyId, newAlternateCost);
                      }
                    } else {
                      // we've gone over budget, abandon this gear combination path
                      reject = true;
                      internalRejections.overUpgradeBudget += 1;
                      internalRejections.overBudgetCurrencies.add(currencyId);
                      break;
                    }
                  }
                }
              }
              if (reject) {
                leaves += 1;
                internalRejectedAt[candidateLen] += 1;
                // eslint-disable-next-line no-continue
                continue;
              }
            }

            const newCandidate = candidate.slice();
            newCandidate[left[0].origIdx] = newItem;

            walk(
              newLeft,
              newCandidate,
              candidateLen + 1,
              // the "next" copies may have never been instantiated if they were never needed
              // in that case, pass along the original
              nextUniqueIds || uniqueIds,
              nextItemLimitCounts || itemLimitCounts,
              nextUniqueGems || uniqueGems,
              nextUpgradeCosts || upgradeCosts,
            );
          }
        }
      };

      walk(data);
      walkCalls = calls;
      partialsExplored = partials;
      leavesExplored = leaves;
      // eslint-disable-next-line no-unused-vars
      rejections = internalRejections;
      // eslint-disable-next-line no-unused-vars
      rejectedAt = internalRejectedAt;

      //devTable(rejectedAt);
      //devTable(rejections);

      return combinations.slice(0, nextCombo);
    };

    // default strategy - fewest slot items first
    const strategies = ['size-asc'];

    /*
    const testStrategies = [
      'size-asc',
    ];

    if (DEVMODE) {
      strategies = testStrategies;
    }
    // */

    let combinations;

    const results = [];
    _.each(strategies, strategyName => {
      let strategy;
      switch (strategyName) {
        case 'size-asc':
          strategy = (rawData) => _.sortBy(rawData, items => (items?.data.length || 0));
          break;
        case 'size-desc':
          strategy = (rawData) => _.sortBy(rawData, items => -(items?.data.length || 0));
          break;
        default:
          break;
      }

      const now = getNow();
      combinations = getCombos(cpArgs, {
        sortStrategy: strategy,
      });
      const duration = getNow() - now;

      const cartProductSize = Combinatorics.cartesianProduct(...cpArgs).length;

      results.push({
        strategy: strategyName,
        numCombos: combinations.length.toLocaleString(),
        calls: walkCalls.toLocaleString(),
        leaves: leavesExplored.toLocaleString(),
        explored: partialsExplored.toLocaleString(),
        cpLength: cartProductSize.toLocaleString(),
        pctEvaluated: _.round(100 * (leavesExplored / cartProductSize), 4),
        time: _.round(duration, 1).toLocaleString(),
        timePerExplored: _.round((duration / partialsExplored), 5),
      });
    });


    /*
    const now = getNow();
    cp.toArray();
    const duration = getNow() - now;

    results.push({
      strategy: 'js-combinatorics cartesianProduct',
      numCombos: cp.length,
      calls: null,
      time: _.round(duration, 1),
    });
    */
    //devTable(_.sortBy(results, 'time'));

    if (tooLarge) {
      return {
        combinations: Infinity,
      };
    }

    return {
      combinations,
      rejections,
    };

    //return cp;
  } catch (error) {
    // eslint-disable-next-line
    console.error(error);
    return {
      combinations: [],
    };
  }
}

const slotIndexMap = [
  'head',
  'neck',
  'shoulder',
  'back',
  'chest',
  'wrist',
  'hands',
  'waist',
  'legs',
  'feet',
  'finger1',
  'finger2',
  'trinket1',
  'trinket2',
  'main_hand',
  'off_hand',
];

let cacheStats;

// ugh, worst function params ever
// This function is also highly optimized and avoids lodash
// All the linting exceptions are intended to try to exit early whenever possible and reduce loops
// over the data.
export function getCombinations(
  rawCombinations,
  lockedRing,
  lockedTrinket,
  classId,
  spec,
  selectedEnhancements,
  replaceEnhancements,
  forceMainStat,
  equippedGear,
  equippedTalents,
  itemSetRequirements,
  requireAlgariColors,
  rawCombinationsMax,
) {
  const perfStart = Date.now();

  // rawCombinationMax could be explicit undefined from a selector
  const maxRaw = rawCombinationsMax || SharedConfig.optimize.maxCombinations;

  const cartesianProduct = rawCombinations.combinations;

  if (cartesianProduct === null) {
    return {
      combinations: [],
    };
  }
  if (cartesianProduct.length === 0) {
    return {
      combinations: [],
    };
  }
  if (cartesianProduct === Infinity) {
    return {
      combinations: null,
      rawCombos: Infinity,
    };
  }

  // things that can make combos > rawCombos (multipliers)
  //  * gems

  // things that can make combos < rawCombos (filters)
  //  * unique equipped
  //  * duplicate gem setups
  //  * duplicate enchant setups

  if (cartesianProduct.length > maxRaw) {
    // generating combos will take significant time.
    // just bail out and use approximations
    return {
      combinations: null,
      rawCombos: cartesianProduct.length,
    };
  }

  let setKeyTime = 0;
  let gemComboTime = 0;
  let gemTestTime = 0;
  let makeGemTime = 0;

  const fulfilledConstraints = {
    uniqueItems: false,
    canFillSockets: true,
    hasFilledSockets: false,
    itemSetRequirement: false,
    enoughAlgariColors: false,
    notEnoughAlgariColors: false,
  };

  const metadata = {
    maxPrismaticSockets: 0,
  };

  cacheStats = {
    hit: 0,
    miss: 0,
    generateTime: 0,
  };

  const result = [];
  const gemCombinationCache = {};
  const usedItems = new Set();

  const specDetails = getSpecInfo(classId, spec) || {};

  const specSelectedEnhancements = _.filter(selectedEnhancements, { specId: specDetails.id });
  const selectedGems = _.map(
    _.filter(specSelectedEnhancements, { slot: 'socket' }),
    g => {
      const gem = getGemDetailsByEnchantId(g.enhancementId);
      return {
        ...g,
        unique: gem?.unique || false,
        gem,
      };
    },
  );

  // We only "collect" sockets for action later if the user has selections for that socket type.
  // This prevents some cases where the gem combinator would remove gems in a surprising way
  const selectedGemsBySocketType = _.groupBy(selectedGems, 'gem.type');
  const collectSocketType = _.mapValues(selectedGemsBySocketType, gems => gems.length > 0);

  const specForceMainStat = _.get(_.find(forceMainStat, { specId: specDetails.id }), 'value', false);
  const specRequireAlgariColors = _.some(selectedGems, 'unique') && specForceMainStat && requireAlgariColors;
  const setKeys = new Set();

  let tooManyCombos = false;

  const removals = {
    uniqueEquipped: 0,
    itemLimit: 0,
    uniqueGem: 0,
    lockedRing: 0,
    lockedTrinket: 0,
    setKey: 0,
    invalidGemCombo: 0,
  };

  const processGearset = (gearset, equipped = false) => {
    if (tooManyCombos || result.length > SharedConfig.optimize.maxTotalCombinations) {
      if (!tooManyCombos) {
        tooManyCombos = true;
      }
      return false;
    }

    // flatten to just get a simple array of items
    const set = [
      gearset[0],
      gearset[1],
      gearset[2],
      gearset[3],
      gearset[4],
      gearset[5],
      gearset[6],
      gearset[7],
      gearset[8],
      gearset[9],
      gearset[10][0], // ring 1
      gearset[10][1], // ring 2
      gearset[11][0], // trinket 1
      gearset[11][1], // trinket 2
      gearset[12][0],
      gearset[12][1] || {},

      // gearset[13] is talents
    ];

    const socketSlots = [];
    const filledSocketSlots = [];
    const uniqueGemsUsed = new Set();
    const itemSets = {};

    for (let i = 0; i < set.length; i += 1) {
      const item = set[i];

      if (!item) {
        // eslint-disable-next-line no-continue
        continue;
      }

      const itemSetId = item.itemSetId;
      if (itemSetId) {
        if (!itemSets[itemSetId]) {
          itemSets[itemSetId] = 0;
        }
        itemSets[itemSetId] += 1;
      }

      _.each(item.socketInfo, (socketInfo, socketType) => {
        if (collectSocketType[socketType] && socketInfo.total > 0) {
          for (let k = 0; k < socketInfo.total; k += 1) {
            if (replaceEnhancements || socketInfo.filled <= k) {
              socketSlots.push({ idx: i, type: socketType, socket: k });
            } else {
              filledSocketSlots.push({
                idx: i,
                type: socketType,
                gem: socketInfo.gems[k],
                socketInfo,
                socket: k,
              });
              const gem = socketInfo.gems[k];
              if (gem.unique) {
                uniqueGemsUsed.add(gem.unique);
              }
            }
          }
        }
        return true;
      });
    }


    // check for locked trinket and ring
    if (lockedRing !== null) {
      if (set[10].origIdx !== lockedRing && set[11].origIdx !== lockedRing) {
        removals.lockedRing += 1;
        return false;
      }
    }
    if (lockedTrinket !== null && set[12] && set[13]) {
      if (set[12].origIdx !== lockedTrinket && set[13].origIdx !== lockedTrinket) {
        removals.lockedTrinket += 1;
        return false;
      }
    }

    // check for item set requirements, only for non-equipped sets
    if (!equipped) {
      let valid = true;
      _.each(itemSetRequirements, (num, itemSetId) => {
        const numItems = itemSets[itemSetId] || 0;
        if (numItems < num) {
          valid = false;
        }
      });
      if (!valid) {
        return false;
      }
      fulfilledConstraints.itemSetRequirement = true;
    }


    const gear = {
      head: gearset[0],
      neck: gearset[1],
      shoulder: gearset[2],
      back: gearset[3],
      chest: gearset[4],
      wrist: gearset[5],
      hands: gearset[6],
      waist: gearset[7],
      legs: gearset[8],
      feet: gearset[9],
      finger1: gearset[10][0],
      finger2: gearset[10][1],
      trinket1: gearset[11][0],
      trinket2: gearset[11][1],
      main_hand: gearset[12][0],
      off_hand: gearset[12][1],
    };

    // we now have the base gear, time to figure out gem/corruption variation candidates

    const candidates = [];

    ////////////////////////////////////////////////////////////////
    //
    // fill in gem slots
    //

    const gemComboStart = getNow();

    let gemCombos = [null];
    // number of empty sockets
    const numSockets = socketSlots.length;
    const socketSlotsSorted = _.sortBy(socketSlots, 'type');
    // total number of prismatic, empty and filled
    const numTotalPrismatic = _.size(
      _.filter(
        _.union(filledSocketSlots, socketSlotsSorted),
        { type: SOCKET_TYPES.PRISMATIC },
      ),
    );
    if (numTotalPrismatic > metadata.maxPrismaticSockets) {
      metadata.maxPrismaticSockets = numTotalPrismatic;
    }
    if (!equipped) {
      if (numSockets > 0 && specSelectedEnhancements && selectedGems.length > 0) {
        const socketLayout = _.map(socketSlotsSorted, 'type');
        const filledSocketLayout = _.filter(_.map(filledSocketSlots, slot => {
          const gem = slot.gem;
          if (requireAlgariColors) {
            return gem.unique || gem.algariColor;
          }
          return gem.unique;
        }));
        const socketLayoutKey = [
          ...filledSocketLayout.sort(),
          ...socketLayout,
        ].join('-');
        if (!gemCombinationCache[socketLayoutKey]) {
          let forceMain = specForceMainStat;
          let gems = selectedGems;
          // this is only populated if "replace existing" is not selected
          if (uniqueGemsUsed.size > 0) {
            forceMain = false;
            // make sure we remove possible gems that already have a unique version in the gear
            gems = _.reject(selectedGems, gem => {
              return uniqueGemsUsed.has(gem.unique);
            });
          }

          const useSimpleMode = !specRequireAlgariColors;
          gemCombinationCache[socketLayoutKey] = getGemCombinations(
            gems,
            forceMain,
            socketLayout,
            useSimpleMode,
          );


          if (specRequireAlgariColors) {
            // only keep gem layouts that have
            // * 4+ colors
            // * Make sure only 1 gem gets multiples
            // this should keep combinations in check when there are 9 sockets
            // e.g.
            // Allow REOSA AAAA
            // Allow REOSA RRRR
            // Deny  REOSA AAAR (mixed after 5 colors)
            // Deny  REOSA AREO (mixed after 5 colors)
            // etc
            const extantGems = _.flatten(_.map(filledSocketSlots, 'gem'));
            const extantAlgariColors = new Set(_.filter(_.map(extantGems, 'algariColor')));
            const extantGemIds = new Set(_.map(extantGems, 'id'));
            gemCombinationCache[socketLayoutKey] = _.filter(gemCombinationCache[socketLayoutKey], layouts => {
              const colors = new Set(extantAlgariColors);
              const gemIds = new Set(extantGemIds);
              let dupeGemId = null;
              for (let i = 0; i < layouts.length; i += 1) {
                const gem = layouts[i];
                const gemId = gem.gem;

                const color = gem.gemDetail.gem.algariColor;
                if (color) {
                  colors.add(color);
                }
                if (!gemIds.has(gemId)) {
                  gemIds.add(gemId);
                } else if (!dupeGemId) {
                  dupeGemId = gemId;
                } else if (gemId !== dupeGemId) {
                  return false;
                }
              }
              // Lower minimum when you have fewer sockets. 4 colors is the maximum
              const minColors = _.min([numTotalPrismatic - 1, ALGARI_COLOR_COMBO_MAX]);
              return colors.size >= minColors;
            });
            if (gemCombinationCache[socketLayoutKey].length > 0) {
              fulfilledConstraints.enoughAlgariColors = true;
            } else {
              fulfilledConstraints.notEnoughAlgariColors = true;
            }
          }
        }
        gemCombos = gemCombinationCache[socketLayoutKey];
        if (gemCombos.length === 0) {
          fulfilledConstraints.canFillSockets = false;
        } else {
          fulfilledConstraints.hasFilledSockets = true;
        }
      }
    }

    for (let i = 0; i < gemCombos.length; i += 1) {
      // get a copy of this array because we gonna mutate it
      const gemCombo = gemCombos[i] && gemCombos[i].slice();

      let comboGear = gear;
      let validGemCombo = true;

      if (gemCombo) {
        // match up gems that already match so we don't just shift gems around
        const gemTestStart = getNow();
        const slotsToFill = [];
        for (let j = 0; j < numSockets; j += 1) {
          const slotIndex = socketSlotsSorted[j].idx;
          const slotType = socketSlotsSorted[j].type;
          const slotName = slotIndexMap[slotIndex];
          const slotItem = comboGear[slotName];
          const socketIndex = socketSlotsSorted[j].socket;

          // does this item have this type of socket?
          const itemTypeSocket = slotItem.socketInfo[slotType];
          if (itemTypeSocket?.total > 0) {
            const slotItemGem = itemTypeSocket.gemIds[socketIndex];
            const gemComboIdx = _.indexOf(gemCombo, { gem: slotItemGem, type: slotType });
            if (gemComboIdx > -1) {
              // remove gem from combo, already in gear
              gemCombo.splice(gemComboIdx, 1);
            } else {
              slotsToFill.push({ idx: slotIndex, type: slotType, socketIndex });
            }
          }
        }

        gemTestTime += getNow() - gemTestStart;

        // fill the rest of the slots
        if (slotsToFill.length > 0) {
          const slotGems = {};
          const changedItems = new Set();
          // preload slotGems with existing gems that we are keeping
          for (let j = 0; j < filledSocketSlots.length; j += 1) {
            const slot = filledSocketSlots[j];
            const slotName = slotIndexMap[slot.idx];
            if (!slotGems[slotName]) {
              slotGems[slotName] = [];
            }
            slotGems[slotName].push(slot.socketInfo.gems[slot.socket].itemId);
          }
          for (let j = 0; j < slotsToFill.length; j += 1) {
            const slotIndex = slotsToFill[j].idx;
            const slotType = slotsToFill[j].type;
            const slotName = slotIndexMap[slotIndex];

            changedItems.add(slotName);

            const targetItem = comboGear[slotName];

            const candidateGem = gemCombo[j].gemDetail.gem;
            const candidateGemId = candidateGem.itemId;

            if (uniqueGemsUsed.has(candidateGem.unique)) {
              validGemCombo = false;
              break;
            }


            // TODO: This might not work for multi-socket items that are partially filled when not using replace
            // existing
            let newGemId;
            // Optimized loop since this is run super frequently
            const keys = Object.keys(targetItem.socketInfo);
            for (let keyIdx = 0; keyIdx < keys.length; keyIdx += 1) {
              const socketType = keys[keyIdx];
              if (socketType === slotType) {
                // get the gem from the generated combo
                newGemId = candidateGemId;
              }
            }

            if (!slotGems[slotName]) {
              slotGems[slotName] = [];
            }
            slotGems[slotName].push(newGemId);
          }

          const makeGemStart = getNow();

          // copy the whole thing once right now, then just change individual members
          comboGear = { ...comboGear };

          _.each(slotGems, (gems, slotName) => {
            if (changedItems.has(slotName)) {
              const targetItem = comboGear[slotName];
              const gemStr = gems.join('/');
              const item = getGemItem(targetItem, gemStr);
              comboGear[slotName] = item;
            }
          });

          makeGemTime += getNow() - makeGemStart;
        }
      }

      if (validGemCombo) {
        candidates.push(comboGear);
      } else {
        removals.invalidGemCombo += 1;
      }
    }

    gemComboTime += getNow() - gemComboStart;

    ////////////////////////////////////////////////////////////////
    //
    // De-duplicate new candidate(s) against the global setKeys
    //

    const setKeyStart = getNow();
    _.each(candidates, candidateGear => {
      const overrides = [];
      _.each(candidateGear, (item, slot) => {
        if (item && item.override) {
          overrides.push({
            slot,
            override: item.override,
          });
        }
      });

      let combo;
      // switch for compatibility for sept 26 talent combination update
      if (gearset[13]) {
        // new version, has talents
        combo = {
          gear: candidateGear,
          talents: gearset[13],
          overrides: overrides.length > 0 ? overrides : undefined,
        };
      } else {
        // old version, just gear
        combo = candidateGear;
      }

      ////////////////////////////////////////////////////////////////
      //
      // generate a "key" for this gearset that will remove things like duplicate ring enchants
      // Need to include anything a user can change/select
      //  * selected items
      //  * weapon enchant
      //  * ring enchants
      //  * talents
      //
      // Gem combos happen after this so we can ignore them in this bit
      //

      const gearCheck = combo.gear;
      if (gearCheck) {
        const selectedIndexes = [];
        const gems = [];
        // TODO: Maybe generalize this a little bit more, have some kidn of category of "simple stat gem/enchant"
        const ringGemStats = {
          crit: 0,
          haste: 0,
          mastery: 0,
          vers: 0,
          stragiint: 0,
        };
        _.each(gearCheck, (item, slot) => {
          if (item) {
            selectedIndexes.push(item.selectedIdx);
            if (item.gem_id) {
              if (item.socketInfo?.PRISMATIC) {
                for (let i = 0; i < item.socketInfo.PRISMATIC.gems.length; i += 1) {
                  const statGem = item.socketInfo.PRISMATIC.gems[i];
                  let statInfo = statGem?.stats || statGem?.stat;
                  if (statInfo) {
                    if (!_.isArray(statInfo)) {
                      statInfo = [statInfo];
                    }
                    _.each(statInfo, stat => {
                      ringGemStats[stat.type] += stat.amount || 0;
                    });
                  }
                  if (statGem.spellEffects) {
                    _.each(statGem.spellEffects, effectId => {
                      if (!ringGemStats[effectId]) {
                        ringGemStats[effectId] = 0;
                      }
                      ringGemStats[effectId] += 1;
                    });
                  }
                }
              } else {
                gems.push(item.gem_id);
              }
            }
            if ((slot === 'finger1' || slot === 'finger2') && item.enchant_id) {
              const enchant = item.parsed?.enchant;
              let statInfo = enchant?.stats || enchant?.stat;
              if (statInfo) {
                if (!_.isArray(statInfo)) {
                  statInfo = [statInfo];
                }
                _.each(statInfo, stat => {
                  ringGemStats[stat.type] += stat.amount || 0;
                });
              }
              _.each(enchant?.spellEffects, effectId => {
                if (!ringGemStats[effectId]) {
                  ringGemStats[effectId] = 0;
                }
                ringGemStats[effectId] += 1;
              });
            }
          }
        });

        const setKey = [
          // item IDs
          selectedIndexes.join('/'),

          // enchants
          (gearCheck.main_hand && gearCheck.main_hand.parsed && gearCheck.main_hand.parsed.enchant_id) || 0,
          (gearCheck.off_hand && gearCheck.off_hand.parsed && gearCheck.off_hand.parsed.enchant_id) || 0,
          (gearCheck.back && gearCheck.back.parsed && gearCheck.back.parsed.enchant_id) || 0,
          (gearCheck.hands && gearCheck.hands.parsed && gearCheck.hands.parsed.enchant_id) || 0,
          (gearCheck.chest && gearCheck.chest.parsed && gearCheck.chest.parsed.enchant_id) || 0,
          (gearCheck.wrist && gearCheck.wrist.parsed && gearCheck.wrist.parsed.enchant_id) || 0,
          (gearCheck.legs && gearCheck.legs.parsed && gearCheck.legs.parsed.enchant_id) || 0,
          (gearCheck.feet && gearCheck.feet.parsed && gearCheck.feet.parsed.enchant_id) || 0,
          (gearCheck.waist && gearCheck.waist.parsed && gearCheck.waist.parsed.enchant_id) || 0,
          (gearCheck.head && gearCheck.head.parsed && gearCheck.head.parsed.enchant_id) || 0,

          // gems
          gems.sort().join('/'),

          // stats from simple gems/enchants
          _.join(_.toPairs(ringGemStats, p => _.join(p, ':')), '/'),

          combo.talents,
        ].join('|');

        // add combination if not already seen
        if (!setKeys.has(setKey)) {
          setKeys.add(setKey);

          // add items to usedItems cache
          _.each(combo.gear, (item, slot) => {
            if (item) {
              usedItems.add(`${slot}:${item.selectedIdx}`);
            }
          });

          result.push(combo);
        } else {
          removals.setKey += 1;
        }
      }
    });
    setKeyTime += getNow() - setKeyStart;

    return true;
  };

  // only prepend equipped if frontend sent it
  if (equippedGear) {
    let expansionSlot;
    const equipped = [
      decorateItem(equippedGear.head[0]),
      decorateItem(equippedGear.neck[0]),
      decorateItem(equippedGear.shoulder[0]),
      decorateItem(equippedGear.back[0]),
      decorateItem(equippedGear.chest[0]),
      decorateItem(equippedGear.wrist[0]),
      decorateItem(equippedGear.hands[0]),
      decorateItem(equippedGear.waist[0]),
      decorateItem(equippedGear.legs[0]),
      decorateItem(equippedGear.feet[0]),
      _.map(equippedGear.rings, decorateItem),
      _.map(equippedGear.trinkets, decorateItem),
      _.map([equippedGear.main_hand[0], equippedGear.off_hand[0]], decorateItem),
      equippedTalents,
      expansionSlot,
    ];

    processGearset(equipped, true);
  }

  cartesianProduct.forEach(gearset => processGearset(gearset, false));

  ////////////////////////////////////////////////////////////////
  //
  // finish up
  //

  // messy way to flip this if sockets have been filled at some point
  if (fulfilledConstraints.hasFilledSockets) {
    fulfilledConstraints.canFillSockets = true;
  }
  // flip this at the end if the option wasn't chosen
  if (!specRequireAlgariColors) {
    fulfilledConstraints.enoughAlgariColors = true;
  }
  // unable to make combos because of a gem issue
  if (result.length === 0) {
    fulfilledConstraints.canFillSockets = false;
  }

  const perfEnd = Date.now();
  const duration = perfEnd - perfStart;

  if (tooManyCombos) {
    return {
      combinations: null,
      fulfilledConstraints,
      removals,
      stats: null,
      metadata,
    };
  }

  const stats = {
    rawCombos: cartesianProduct.length,
    filteredCombos: result.length,
    time: duration,
    cacheStats,
    setKeyTime,
    gemComboTime,
    makeGemTime,
    gemTestTime,
  };

  const slotMap = {
    finger1: 'rings',
    finger2: 'rings',
    trinket1: 'trinkets',
    trinket2: 'trinkets',
  };

  return {
    combinations: result,
    fulfilledConstraints,
    removals,
    stats,
    metadata,
    usedItems: _.map([...usedItems], key => {
      const parts = _.split(key, ':');
      return {
        slot: slotMap[parts[0]] || parts[0],
        index: parseInt(parts[1], 10),
      };
    }),
  };
}
